import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Wand2,
  RefreshCw,
  Save,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Shared block-editor for the 3 Lazy-AI playbooks (Skool, Monetization, Launch Kit).
 * Fetches from GET /api/admin/playbooks/{kind}. Runs full generation via
 * POST .../generate. Regenerates or edits individual blocks. Renders any
 * additional buttons via the `extraActions` render-prop.
 */
export default function Playbook({
  kind,
  eyebrow,
  headline,
  tagline,
  emptyCTA = "Generate the whole playbook",
  extraActions,
  skoolFormatter,
}) {
  const [state, setState] = useState({
    loaded: false,
    generated: false,
    title: "",
    blocks: [],
    specCount: 0,
  });
  const [busyAll, setBusyAll] = useState(false);
  const [busyBlock, setBusyBlock] = useState(null); // slug or "all"
  const [openBlock, setOpenBlock] = useState(null);
  const [drafts, setDrafts] = useState({}); // slug -> edited body
  const [copiedSlug, setCopiedSlug] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/playbooks/${kind}`);
      setState({
        loaded: true,
        generated: !!r.data.generated,
        title: r.data.title,
        blocks: r.data.blocks || [],
        specCount: r.data.spec_count || 0,
      });
      // Seed drafts from server-side bodies
      const nextDrafts = {};
      for (const b of r.data.blocks || []) nextDrafts[b.slug] = b.body || "";
      setDrafts(nextDrafts);
    } catch (e) {
      toast.error("Could not load playbook", { description: readApiError(e) });
      setState((s) => ({ ...s, loaded: true }));
    }
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  const generateAll = async () => {
    setBusyAll(true);
    try {
      await api.post(`/admin/playbooks/${kind}/generate`);
      toast.success("Playbook generated", { description: "All blocks written in your voice." });
      await load();
    } catch (e) {
      toast.error("Generation failed", { description: readApiError(e) });
    } finally {
      setBusyAll(false);
    }
  };

  const regenerateBlock = async (slug) => {
    setBusyBlock(slug);
    try {
      const r = await api.post(`/admin/playbooks/${kind}/${slug}/regenerate`);
      toast.success(`Rewrote: ${r.data.title}`);
      setState((s) => ({
        ...s,
        blocks: s.blocks.map((b) => (b.slug === slug ? r.data : b)),
      }));
      setDrafts((d) => ({ ...d, [slug]: r.data.body || "" }));
    } catch (e) {
      toast.error("Rewrite failed", { description: readApiError(e) });
    } finally {
      setBusyBlock(null);
    }
  };

  const saveBlock = async (slug) => {
    setBusyBlock(slug);
    try {
      const body = drafts[slug] ?? "";
      const r = await api.put(`/admin/playbooks/${kind}/${slug}`, { body });
      toast.success("Saved");
      setState((s) => ({
        ...s,
        blocks: s.blocks.map((b) => (b.slug === slug ? r.data : b)),
      }));
    } catch (e) {
      toast.error("Save failed", { description: readApiError(e) });
    } finally {
      setBusyBlock(null);
    }
  };

  const copyBlock = async (block) => {
    const body = drafts[block.slug] ?? block.body ?? "";
    try {
      await navigator.clipboard.writeText(body);
      setCopiedSlug(block.slug);
      setTimeout(() => setCopiedSlug(null), 1400);
      toast.success("Copied", { description: block.title });
    } catch {
      toast.error("Copy failed");
    }
  };

  const copyBlockForSkool = async (block) => {
    if (!skoolFormatter) return;
    const body = drafts[block.slug] ?? block.body ?? "";
    const md = skoolFormatter({ ...block, body });
    try {
      await navigator.clipboard.writeText(md);
      setCopiedSlug(`skool-${block.slug}`);
      setTimeout(() => setCopiedSlug(null), 1400);
      toast.success("Copied for Skool", { description: block.title });
    } catch {
      toast.error("Copy failed");
    }
  };

  const status = useMemo(() => {
    if (!state.loaded) return "loading";
    if (busyAll) return "generating";
    if (!state.generated) return "empty";
    return "ready";
  }, [state.loaded, state.generated, busyAll]);

  return (
    <div className="space-y-8" data-testid={`playbook-${kind}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {eyebrow && (
            <p className="admin-eyebrow">{eyebrow}</p>
          )}
          <h1 className="admin-h1 mt-2">
            {headline}
          </h1>
          {tagline && <p className="mt-3 text-sm text-[#C9BEA8]">{tagline}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {state.loaded && (
            <Button
              onClick={generateAll}
              disabled={busyAll}
              data-testid={`playbook-${kind}-generate-all`}
              className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
            >
              {busyAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Writing…
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" />
                  {state.generated ? "Regenerate all" : emptyCTA}
                </>
              )}
            </Button>
          )}
          {typeof extraActions === "function" && extraActions({ kind, blocks: state.blocks, reload: load })}
        </div>
      </div>

      {status === "loading" && (
        <div className="flex items-center gap-2 text-sm text-[#9A8E78]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading playbook…
        </div>
      )}

      {status === "empty" && (
        <div
          data-testid={`playbook-${kind}-empty`}
          className="rounded-2xl border border-dashed border-[#5E594E] bg-[#5A5347] p-10 text-center"
        >
          <p className="font-serif text-2xl tracking-wide text-[#F5EFE2]">
            Nothing here yet.
          </p>
          <p className="mt-2 text-sm text-[#C9BEA8]">
            Hit <em>{emptyCTA}</em> and the AI will draft {state.specCount} blocks in your voice.
            You can rewrite any of them one-click after.
          </p>
        </div>
      )}

      {status === "generating" && !state.generated && (
        <div className="rounded-2xl border border-[#C9A961]/40 bg-[#5A5347] p-10 text-center">
          <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-[#C9A961]" />
          <p className="font-serif text-xl tracking-wide text-[#F5EFE2]">
            Writing your playbook…
          </p>
          <p className="mt-2 text-xs text-[#C9BEA8]">
            ~{state.specCount} blocks · usually 45-90 seconds
          </p>
        </div>
      )}

      {state.generated && state.blocks.length > 0 && (
        <>
          <div className="admin-section-divider" aria-hidden="true">
            <span className="dot" />
          </div>
          <div className="space-y-3">
          {state.blocks.map((block, idx) => {
            const isOpen = openBlock === block.slug;
            const draft = drafts[block.slug] ?? block.body ?? "";
            const dirty = draft !== (block.body ?? "");
            return (
              <div
                key={block.slug}
                data-testid={`playbook-block-${block.slug}`}
                className="rounded-2xl border border-[#5E594E] bg-[#5A5347] transition-colors hover:border-[#C9A961]/50"
              >
                <button
                  type="button"
                  onClick={() => setOpenBlock(isOpen ? null : block.slug)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#9A8E78]">
                      Block {String(idx + 1).padStart(2, "0")}
                    </p>
                    <p className="mt-0.5 truncate font-serif text-xl tracking-wide text-[#F5EFE2]">
                      {block.title}
                    </p>
                  </div>
                  <span className="shrink-0 text-[#C9A961]">
                    {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-[#5E594E] px-5 py-4">
                    {block.image_slug && (
                      <p className="mb-3 text-[10px] uppercase tracking-wider text-[#9A8E78]">
                        Reference image slug: <span className="text-[#C9A961]">{block.image_slug}</span>
                      </p>
                    )}
                    <Textarea
                      data-testid={`playbook-block-body-${block.slug}`}
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [block.slug]: e.target.value }))}
                      rows={Math.max(6, Math.min(24, Math.ceil((draft?.length || 0) / 90) + 4))}
                      className="w-full border-[#5E594E] bg-[#3D3A33] text-sm leading-relaxed text-[#F5EFE2] placeholder:text-[#9A8E78]"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => saveBlock(block.slug)}
                        disabled={!dirty || busyBlock === block.slug}
                        data-testid={`playbook-block-save-${block.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#C9A961] bg-[#C9A961] px-3 py-1.5 text-xs text-[#3D3A33] transition-colors hover:bg-[#DDBE7A] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Save className="h-3.5 w-3.5" />
                        {dirty ? "Save edits" : "Saved"}
                      </button>
                      <button
                        type="button"
                        onClick={() => regenerateBlock(block.slug)}
                        disabled={busyBlock === block.slug}
                        data-testid={`playbook-block-regen-${block.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#5E594E] bg-[#3D3A33] px-3 py-1.5 text-xs text-[#C9BEA8] transition-colors hover:border-[#C9A961]/60 hover:text-[#C9A961] disabled:opacity-40"
                      >
                        {busyBlock === block.slug ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        Rewrite this block
                      </button>
                      <button
                        type="button"
                        onClick={() => copyBlock(block)}
                        data-testid={`playbook-block-copy-${block.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#5E594E] bg-[#3D3A33] px-3 py-1.5 text-xs text-[#C9BEA8] transition-colors hover:border-[#C9A961]/60 hover:text-[#C9A961]"
                      >
                        {copiedSlug === block.slug ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                        Copy
                      </button>
                      {skoolFormatter && (
                        <button
                          type="button"
                          onClick={() => copyBlockForSkool(block)}
                          data-testid={`playbook-block-copy-skool-${block.slug}`}
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#7A8B6F]/60 bg-[#7A8B6F]/10 px-3 py-1.5 text-xs text-[#B6C2A6] transition-colors hover:bg-[#7A8B6F]/25 hover:text-[#F5EFE2]"
                        >
                          {copiedSlug === `skool-${block.slug}` ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                          Copy for Skool
                        </button>
                      )}
                    </div>
                    {block.updated_at && (
                      <p className="mt-2 text-[10px] text-[#9A8E78]">
                        last saved {new Date(block.updated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <p className="pt-2 text-xs text-[#9A8E78]">
            {state.blocks.length} of {state.specCount} blocks · cached in{" "}
            <code className="text-[#C9A961]">playbook_blocks</code> · edits persist across sessions
          </p>
        </div>
        </>
      )}
    </div>
  );
}
